import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Dayjs } from 'dayjs';
import {
  BriefcaseIcon,
  Building2Icon,
  GlobeIcon,
  Handshake,
  ShieldCheckIcon,
  StarIcon,
  TagIcon,
  TimerIcon,
  UserCheckIcon,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatHoursToTimeString } from '@/helpers/date-format';
import { cn } from '@/lib/utils';
import {
  calculateBillableUtilizationPercentage,
  calculateBillableWorkingHours,
  EntityAvatar,
  getMemberStatusText,
  getUtilizationColor,
  StatusBadge,
  StatusBadgeType,
} from '@/modules/common';
import { MemberStatus } from '@/modules/team';
import { MembershipType } from '@/modules/team/types/membership-type';
import { STANDARD_DATE_FORMAT_DAYJS } from '@/utils/date-formatters/date-formatters';

import {
  parseCustomAttributeColumnKey,
  reconcileUserInfoOrder,
  UserInfoColumnKey,
} from '../constants';
import { calculateCenterTopPosition } from '../helpers/tooltip-helpers';
import usePlannerStore from '../store';
import { PlannerCustomAttributeResponse } from '../types/dto/response/planner-custom-attribute-response';
import { usePlannerTooltip } from './tooltip/planner-tooltip-provider';

type UserInfoDetails = {
  billableCapacityPercent?: number;
  workingMinutesPerDay?: number;
  isManager?: boolean;
  /** Name of the member's assigned manager (null when they have none) */
  memberManagerName?: string | null;
  department?: string;
  role?: string;
  seniority?: string;
  country?: string | null;
  city?: string | null;
  membershipType?: MembershipType | null;
  customAttributes?: PlannerCustomAttributeResponse[];
};

interface PlannerTeamMemberInfoProps {
  title: string;
  status: MemberStatus;
  onDemand: boolean | undefined;
  avatarLink: string | null;
  details?: UserInfoDetails;
  userStartDate: Dayjs | undefined;
  archivedDate: Dayjs | undefined;
}

export const PlannerTeamMemberInfo = memo(
  ({
    title,
    avatarLink,
    onDemand,
    status,
    details,
    userStartDate,
    archivedDate,
  }: PlannerTeamMemberInfoProps) => {
    const { t } = useTranslation(['authenticated.planner'], { keyPrefix: 'userInfo' });
    const { t: tPlanner } = useTranslation(['authenticated.planner']);
    const { showTooltip, hideTooltip } = usePlannerTooltip();
    const userInfoVisibility = usePlannerStore(store => store.userInfoVisibility);
    const userInfoOrder = usePlannerStore(store => store.userInfoOrder);
    // Only re-render when the boolean result changes (flips once when scrolling past user start date)
    const isUserStarted = usePlannerStore(store =>
      userStartDate && store.firstVisibleDate
        ? store.firstVisibleDate.isSameOrAfter(userStartDate)
        : true
    );
    const {
      billableCapacityPercent = 0,
      workingMinutesPerDay = 0,
      isManager = false,
      memberManagerName = null,
      department = t('noDepartment'),
      role = t('noRole'),
      seniority = t('noSeniority'),
      country = null,
      city = null,
      membershipType = null,
      customAttributes = [],
    } = details || {};

    // Reuse the filter option labels for membership type display
    const membershipTypeLabels: Record<MembershipType, string> = {
      [MembershipType.InHouse]: tPlanner('filters.membershipTypes.inHouse'),
      [MembershipType.Partner]: tPlanner('filters.membershipTypes.partner'),
      [MembershipType.Contractor]: tPlanner('filters.membershipTypes.contractor'),
    };
    const workingHoursPerDay = workingMinutesPerDay / 60;
    const billableWorkingHoursPerDay = calculateBillableWorkingHours(
      workingMinutesPerDay,
      billableCapacityPercent
    );

    const utilizationPercentage = calculateBillableUtilizationPercentage(billableCapacityPercent);

    const progressColor = getUtilizationColor(utilizationPercentage);

    const capacity = onDemand ? t('onDemand') : formatHoursToTimeString(workingHoursPerDay);

    const billableCapacity = formatHoursToTimeString(billableWorkingHoursPerDay);

    const isArchived = status === MemberStatus.Archived;

    const createTooltipHandlers = useCallback(
      (content: string) => ({
        onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
          const { centerX, topY } = calculateCenterTopPosition(e.currentTarget);
          showTooltip(content, centerX, topY);
        },
        onMouseLeave: () => hideTooltip(),
      }),
      [showTooltip, hideTooltip]
    );

    // Reorderable icon rows, keyed so the picker's userInfoOrder drives the sequence.
    // Each entry returns its row (or null when hidden / empty) — order is applied below.
    const fixedRowByKey: Record<string, ReactNode> = {
      isManager:
        userInfoVisibility.isManager && isManager ? (
          <div className="flex min-w-0 items-center gap-1.5">
            <ShieldCheckIcon size={14} className="flex-shrink-0 text-violet-500" />
            <span className="truncate text-violet-500">{t('manager')}</span>
          </div>
        ) : null,
      memberManager: userInfoVisibility.memberManager ? (
        <div
          className="flex min-w-0 items-center gap-1.5"
          {...createTooltipHandlers(
            t('tooltip.memberManager', { manager: memberManagerName ?? t('noManager') })
          )}
        >
          <UserCheckIcon size={14} className="flex-shrink-0" />
          <span className="truncate">{memberManagerName ?? t('noManager')}</span>
        </div>
      ) : null,
      department: userInfoVisibility.department ? (
        <div
          className="flex min-w-0 items-center gap-1.5"
          {...createTooltipHandlers(t('tooltip.department', { department }))}
        >
          <Building2Icon size={14} className="flex-shrink-0" />{' '}
          <span className="truncate">{department}</span>
        </div>
      ) : null,
      role: userInfoVisibility.role ? (
        <div
          className="flex min-w-0 gap-1.5"
          {...createTooltipHandlers(t('tooltip.role', { role }))}
        >
          <BriefcaseIcon size={14} className="flex-shrink-0" />{' '}
          <span className="truncate">{role}</span>
        </div>
      ) : null,
      seniority: userInfoVisibility.seniority ? (
        <div
          className="flex min-w-0 gap-1.5"
          {...createTooltipHandlers(t('tooltip.seniority', { seniority }))}
        >
          <StarIcon size={14} className="flex-shrink-0" />{' '}
          <span className="truncate">{seniority}</span>
        </div>
      ) : null,
      location:
        userInfoVisibility.location && country ? (
          <div
            className="flex min-w-0 gap-1.5"
            {...createTooltipHandlers(city ? t('tooltip.location', { city, country }) : country)}
          >
            <GlobeIcon size={14} className="flex-shrink-0" />
            <span className="truncate">{city ? `${city}, ${country}` : country}</span>
          </div>
        ) : null,
      capacityPerDay:
        !isArchived && userInfoVisibility.capacityPerDay ? (
          <div
            className="flex min-w-0 gap-1.5"
            {...createTooltipHandlers(
              onDemand ? t('tooltip.onDemand') : t('tooltip.capacity', { capacity })
            )}
          >
            <TimerIcon size={14} className="flex-shrink-0" />
            <span className="truncate">{capacity}</span>
          </div>
        ) : null,
      membershipType:
        userInfoVisibility.membershipType && membershipType ? (
          <div
            className="flex min-w-0 gap-1.5"
            {...createTooltipHandlers(
              t('tooltip.membershipType', {
                membershipType: membershipTypeLabels[membershipType],
              })
            )}
          >
            <Handshake size={14} className="flex-shrink-0" />
            <span className="truncate">{membershipTypeLabels[membershipType]}</span>
          </div>
        ) : null,
    };

    const renderCustomAttributeRow = (attributeId: number): ReactNode => {
      const attribute = customAttributes.find(candidate => candidate.id === attributeId);
      const isVisible = userInfoVisibility.customAttributes[String(attributeId)];
      if (!attribute || !isVisible || attribute.values.length === 0) {
        return null;
      }
      const attributeValues = attribute.values.join(', ');
      return (
        <div
          className="flex min-w-0 gap-1.5"
          {...createTooltipHandlers(`${attribute.name}: ${attributeValues}`)}
        >
          <TagIcon size={14} className="flex-shrink-0" />
          <span className="truncate">{attributeValues}</span>
        </div>
      );
    };

    const orderedColumnKeys = useMemo(
      () =>
        reconcileUserInfoOrder(
          userInfoOrder,
          customAttributes.map(attribute => attribute.id)
        ),
      [userInfoOrder, customAttributes]
    );

    const orderedRows: Array<{ key: string; node: ReactNode }> = [];
    orderedColumnKeys.forEach((key: UserInfoColumnKey) => {
      const attributeId = parseCustomAttributeColumnKey(key);
      const node =
        attributeId !== null ? renderCustomAttributeRow(attributeId) : fixedRowByKey[key];
      if (node) {
        orderedRows.push({ key, node });
      }
    });

    // Truncated tooltip for title
    const [isTitleTruncated, setIsTitleTruncated] = useState(false);
    const titleRef = useRef<HTMLDivElement>(null);

    const checkTruncation = useCallback(() => {
      if (titleRef.current) {
        const { scrollWidth, offsetWidth } = titleRef.current;
        setIsTitleTruncated(scrollWidth > offsetWidth);
      }
    }, []);

    useEffect(() => {
      checkTruncation();
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [checkTruncation, title]);

    const handleTitleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        checkTruncation();
        if (isTitleTruncated) {
          const { centerX, topY } = calculateCenterTopPosition(e.currentTarget);
          showTooltip(title, centerX, topY);
        }
      },
      [checkTruncation, isTitleTruncated, title, showTooltip]
    );

    return (
      <div className="flex w-full max-w-full flex-col items-start gap-2 overflow-hidden">
        <div className="flex w-full items-start gap-1.5">
          <EntityAvatar
            image={avatarLink}
            name={title}
            shape="rounded"
            size="sm"
            className="flex-shrink-0"
          />
          <div className="flex min-w-0 max-w-40 flex-1 flex-col gap-2.5">
            <div className="flex flex-col">
              <div
                ref={titleRef}
                className={cn('block w-full truncate text-sm font-medium')}
                onMouseEnter={handleTitleMouseEnter}
                onMouseLeave={hideTooltip}
              >
                {title}
              </div>
              <div>
                {isUserStarted ? (
                  <StatusBadge
                    type={StatusBadgeType.TeamMember}
                    status={status}
                    label={getMemberStatusText(status)}
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">{t('userNotJoined')}</span>
                )}
              </div>
            </div>
            {!isUserStarted && (
              <span className="grid whitespace-normal break-keep text-xs text-muted-foreground">
                <Trans
                  i18nKey="authenticated.planner:userInfo.userWillBeAvailable"
                  values={{
                    date: userStartDate?.format(STANDARD_DATE_FORMAT_DAYJS),
                  }}
                  components={{ strong: <strong /> }}
                />
              </span>
            )}
            {details && isUserStarted && (
              <>
                <div className="flex flex-col gap-1.5 text-xs font-normal">
                  {orderedRows.map(row => (
                    <div key={row.key}>{row.node}</div>
                  ))}
                </div>
                {isArchived ? (
                  <span className="grid whitespace-normal break-keep text-xs text-muted-foreground">
                    <Trans
                      i18nKey="authenticated.planner:userInfo.memberArchived"
                      values={{
                        date: archivedDate?.format(STANDARD_DATE_FORMAT_DAYJS),
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                ) : (
                  userInfoVisibility.billableCapacity &&
                  !onDemand && (
                    <>
                      {billableCapacityPercent ? (
                        <div
                          className="flex flex-col"
                          onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                            const { centerX, topY } = calculateCenterTopPosition(e.currentTarget);
                            showTooltip(
                              <Trans
                                i18nKey="tooltip.billableCapacity"
                                t={t}
                                components={{
                                  bold: <b />,
                                }}
                                values={{
                                  billableCapacity,
                                  capacity,
                                }}
                              />,
                              centerX,
                              topY
                            );
                          }}
                          onMouseLeave={() => hideTooltip()}
                        >
                          <div className="flex justify-between gap-1 text-xs">
                            <span>
                              {billableCapacity}
                              {t('perDay')}
                            </span>
                            <span style={{ color: progressColor }}>
                              {t('percentage', {
                                value: utilizationPercentage,
                              })}
                            </span>
                          </div>
                          <Progress
                            value={utilizationPercentage}
                            color="secondary"
                            indicatorColor={progressColor}
                          />
                        </div>
                      ) : (
                        <div>
                          <Badge
                            variant="outline"
                            className="border border-muted-foreground text-xs font-normal text-muted-foreground"
                          >
                            {t('nonBillable')}
                          </Badge>
                        </div>
                      )}
                    </>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);
